/**
 * Token Usage Store
 *
 * Zustand store for managing Pipedrive token usage state.
 * Provides reactive state for token tracking across the application.
 */

import { create } from 'zustand';
import { tokenUsageService, TokenUsageStatus, UsageBreakdown } from '../services/tokenUsageService';

interface TokenUsageStore {
  // State
  /** Current token usage status (null if not loaded) */
  status: TokenUsageStatus | null;
  /** Usage breakdown by endpoint (null if not loaded) */
  breakdown: UsageBreakdown | null;
  /** Loading state for async operations */
  isLoading: boolean;
  /** Error message from last failed operation (null if no error) */
  error: string | null;

  // Actions
  /** Fetch current token usage status from API */
  fetchStatus: () => Promise<void>;
  /** Fetch usage breakdown by endpoint from API */
  fetchBreakdown: (hours?: number) => Promise<void>;
}

/**
 * Token usage store
 *
 * @example
 * ```typescript
 * // In a component
 * import { useTokenUsageStore } from '@shared/store/tokenUsageStore';
 *
 * function TokenDisplay() {
 *   const { status, isLoading, error, fetchStatus } = useTokenUsageStore();
 *
 *   useEffect(() => {
 *     fetchStatus();
 *   }, [fetchStatus]);
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <ErrorMessage message={error} />;
 *   if (!status) return null;
 *
 *   return (
 *     <div>
 *       <h3>Token Usage</h3>
 *       <p>{status.used} / {status.budget} ({status.percentage}%)</p>
 *       <p>Status: {status.status}</p>
 *       <p>Resets in: {status.time_until_reset}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export const useTokenUsageStore = create<TokenUsageStore>((set) => ({
  // Initial state
  status: null,
  breakdown: null,
  isLoading: false,
  error: null,

  /**
   * Fetch current token usage status
   *
   * Sets loading state, fetches from API, and updates status.
   * On error, sets error message and keeps loading false.
   */
  fetchStatus: async () => {
    set({ isLoading: true, error: null });
    try {
      const status = await tokenUsageService.getStatus();
      set({ status, isLoading: false });
      console.log('[TokenUsageStore] Fetched status:', status);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch token usage status';
      set({
        error: errorMessage,
        isLoading: false
      });
      console.error('[TokenUsageStore] Failed to fetch status:', errorMessage);
    }
  },

  /**
   * Fetch usage breakdown by endpoint
   *
   * @param hours - Number of hours to look back (default: 24)
   */
  fetchBreakdown: async (hours = 24) => {
    set({ isLoading: true, error: null });
    try {
      const breakdown = await tokenUsageService.getBreakdown(hours);
      set({ breakdown, isLoading: false });
      console.log('[TokenUsageStore] Fetched breakdown:', breakdown);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch token usage breakdown';
      set({
        error: errorMessage,
        isLoading: false
      });
      console.error('[TokenUsageStore] Failed to fetch breakdown:', errorMessage);
    }
  },
}));
